// Sunrise/sunset from the NOAA general solar position approximation
// (accurate to a minute or two — plenty for theming and scenery).

const rad = (d: number) => (d * Math.PI) / 180;
const deg = (r: number) => (r * 180) / Math.PI;

/**
 * Sunrise and sunset instants for the UTC calendar day containing `day`.
 * Returns nulls during polar day/night.
 */
export function sunTimes(day: Date, lat: number, lon: number): { sunrise: Date | null; sunset: Date | null } {
  const y = day.getUTCFullYear();
  const start = Date.UTC(y, 0, 1);
  const n = Math.floor((Date.UTC(y, day.getUTCMonth(), day.getUTCDate()) - start) / 86_400_000) + 1;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const gamma = ((2 * Math.PI) / (leap ? 366 : 365)) * (n - 1);
  const eqtime =
    229.18 * (0.000075 + 0.001868 * Math.cos(gamma) - 0.032077 * Math.sin(gamma) - 0.014615 * Math.cos(2 * gamma) - 0.040849 * Math.sin(2 * gamma));
  const decl =
    0.006918 -
    0.399912 * Math.cos(gamma) +
    0.070257 * Math.sin(gamma) -
    0.006758 * Math.cos(2 * gamma) +
    0.000907 * Math.sin(2 * gamma) -
    0.002697 * Math.cos(3 * gamma) +
    0.00148 * Math.sin(3 * gamma);
  const cosHa = Math.cos(rad(90.833)) / (Math.cos(rad(lat)) * Math.cos(decl)) - Math.tan(rad(lat)) * Math.tan(decl);
  if (cosHa > 1 || cosHa < -1) return { sunrise: null, sunset: null };
  const ha = deg(Math.acos(cosHa));
  const midnight = Date.UTC(y, day.getUTCMonth(), day.getUTCDate());
  return {
    sunrise: new Date(midnight + (720 - 4 * (lon + ha) - eqtime) * 60_000),
    sunset: new Date(midnight + (720 - 4 * (lon - ha) - eqtime) * 60_000),
  };
}

export type SkyPhase = "night" | "dawn" | "day" | "dusk";

/**
 * Where we are in the day. Uses real sun times when a location is known,
 * otherwise 6:30/18:30 in the given timezone.
 */
export function skyPhase(now: Date, timeZone: string, loc: { lat: number; lon: number } | null): SkyPhase {
  let rise: number;
  let set: number;
  const t = now.getTime();
  if (loc) {
    // Pick the UTC day from local solar time (UTC + lon/15 h), so evening hours
    // in the Americas use that local day's sunrise and sunset.
    const noonish = new Date(t + (loc.lon / 15) * 3_600_000);
    const s = sunTimes(noonish, loc.lat, loc.lon);
    if (!s.sunrise || !s.sunset) return "day";
    rise = s.sunrise.getTime();
    set = s.sunset.getTime();
  } else {
    const hm = Number(new Intl.DateTimeFormat("en-US", { timeZone, hour: "numeric", hour12: false }).format(now)) * 60 +
      Number(new Intl.DateTimeFormat("en-US", { timeZone, minute: "numeric" }).format(now));
    const today = t - hm * 60_000;
    rise = today + 390 * 60_000;
    set = today + 1110 * 60_000;
  }
  const M = 60_000;
  if (t < rise - 50 * M || t > set + 50 * M) return "night";
  if (t < rise + 60 * M) return "dawn";
  if (t > set - 75 * M) return "dusk";
  return "day";
}
